import type {
  ProjectionAverageScoreRequest,
  ProjectionAverageScoreResponse,
  PublishProjectionsRequest,
  PublishProjectionsResponse,
  SingleProjectionAPIResponse,
  UpdateBulkPlayerTeamRequest,
  UpdateBulkProjectionsRequest,
  UpdateProjectionsByCSVRequest
} from '@library/types/projection';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { defaultHeaders } from '@bo-constants/apiHeaders';
import { uniq } from 'ramda';

const { admin } = apiPaths;
const projectionsPath = `${admin}projections`;

export const projectionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPojectionById: build.query<
      SingleProjectionAPIResponse,
      { projection_id: string }
    >({
      query: ({ projection_id }) => ({
        url: `${admin}/models/projections/${projection_id}`,
        method: 'GET',
        headers: defaultHeaders
      }),
      transformResponse: (response: SingleProjectionAPIResponse) => response,
      providesTags: ['Projections']
    }),
    getAverageScore: build.mutation<
      ProjectionAverageScoreResponse[],
      ProjectionAverageScoreRequest[]
    >({
      query: (payload) => ({
        url: `${admin}load_scores`,
        method: 'POST',
        body: { player_score_request: payload },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    }),
    publishProjections: build.mutation<
      PublishProjectionsResponse,
      PublishProjectionsRequest
    >({
      query: (payload) => ({
        url: projectionsPath,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: (_result, _error, arg) => {
        const rankingsTags: Array<{ id: string; type: 'Rankings' }> = uniq(
          arg.projections_request[0].projections.map((projection) => ({
            type: 'Rankings',
            id: String(projection.stat_type_id)
          }))
        );
        const projectionTags: Array<{
          id: string;
          type: 'Projections';
        }> = uniq(
          arg.projections_request.map((projection) => ({
            type: 'Projections',
            id: String(projection.projections[0].game_id ?? 0)
          }))
        );

        return [...rankingsTags, ...projectionTags, 'Board Builder', 'Scores'];
      }
    }),
    updateBulkProjections: build.mutation<
      { updated_ids: Array<number>; invalid_ids: Array<number> },
      UpdateBulkProjectionsRequest
    >({
      query: (payload) => ({
        url: `${admin}projections/bulk_update`,
        method: 'PATCH',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['Rankings', 'Scores', 'Board Builder', 'Projections']
    }),
    updateBulkTeamProjections: build.mutation<
      { message: string; updated_records_count: number },
      UpdateBulkPlayerTeamRequest
    >({
      query: (payload) => ({
        url: `${admin}bulk_update_team_projections`,
        method: 'PATCH',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['Rankings', 'Scores', 'Board Builder', 'Projections']
    }),
    updateBulkPlayerProjections: build.mutation<
      { message: string; updated_records_count: number },
      UpdateBulkPlayerTeamRequest
    >({
      query: (payload) => ({
        url: `${admin}bulk_update_player_projections`,
        method: 'PATCH',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['Rankings', 'Scores', 'Board Builder', 'Projections']
    }),
    uploadProjectionsByCSV: build.mutation<
      unknown[],
      UpdateProjectionsByCSVRequest
    >({
      query: ({ csv, skip_start_time_validation }) => ({
        url: `${admin}upload_projections?skip_start_time_validation=${skip_start_time_validation}`,
        method: 'POST',
        body: { csv },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['Rankings', 'Scores', 'Board Builder', 'Projections']
    })
  })
});

export const {
  useGetPojectionByIdQuery,
  useGetAverageScoreMutation,
  usePublishProjectionsMutation,
  useUpdateBulkProjectionsMutation,
  useUpdateBulkPlayerProjectionsMutation,
  useUpdateBulkTeamProjectionsMutation,
  useUploadProjectionsByCSVMutation
} = projectionsApi;
